'use client';

import { faBrowser, faCalendarDays, faTimes } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Laboratory } from '@medprev-web-app/api-graphql-client';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Modal } from '@/components/ui/modal.component';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { resolveLaboratoryName } from '@/utils/laboratory/laboratory.util';
import { Link } from '@/components/ui/link/link.component';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface BranchLaboratoryRedirectModalProps {
  open: boolean;
  laboratory: Laboratory;
  onOpenChange: (isOpen: boolean) => void;
}

export function BranchLaboratoryRedirectModal({ open, laboratory, onOpenChange }: BranchLaboratoryRedirectModalProps) {
  const { buildLink } = useAgendamentoNavigation();
  const analytics = getAnalyticsPlugin();

  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hasTrackedRef.current = false;
      return;
    }

    if (hasTrackedRef.current) {
      return;
    }

    analytics.track(
      AnalyticsEnum.LABORATORY_PROFILE_REDIRECT_MODAL_SHOWN,
    );

    hasTrackedRef.current = true;
  }, [open, analytics]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      analytics.track(
        AnalyticsEnum.LABORATORY_PROFILE_REDIRECT_MODAL_CANCELED,
      );
    }

    onOpenChange(isOpen);
  };
  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <Modal.Content
        className="px-3 py-5"
        slotHeader={(
          <div className="flex justify-between gap-2 pb-0 items-start w-full">
            <div className="flex items-start gap-2">
              <Icon icon={faBrowser} size="xl" className="text-green-400 mt-1" />
              <span
                data-testid={DataTestIdEnum.BRANCH_LABORATORY_REDIRECT_MODAL_TITLE}
                className="font-bold text-2xl text-gray-900 text-left"
              >
                Aviso de redirecionamento
              </span>
            </div>
            <Modal.Close asChild className="text-gray-500 cursor-pointer self-start px-2 hover:bg-gray-50">
              <Icon
                data-testid={DataTestIdEnum.BRANCH_LABORATORY_REDIRECT_MODAL_CLOSE_BUTTON}
                icon={faTimes}
                size="2xl"
                className="hover:text-gray-900"
              />
            </Modal.Close>
          </div>
        )}
      >
        <div className="mt-4 px-2">
          <p className="text-gray-800" data-testid={DataTestIdEnum.BRANCH_LABORATORY_REDIRECT_MODAL_TEXT}>
            Você será redirecionado para o site de agendamentos online da Medprev, onde você pode ver todos os exames
            com
            {' '}
            {resolveLaboratoryName(laboratory)}
            {' '}
            e fazer seu agendamento. É rápido e seguro. Clique no botão abaixo
            para continuar:
          </p>

          <Button
            data-testid={DataTestIdEnum.BRANCH_LABORATORY_REDIRECT_MODAL_LINK_REDIRECT}
            asChild
            startIcon={faCalendarDays}
            className="mt-6"
            data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_REDIRECT_MODAL_CONFIRMED}
          >
            <Link prefetch={false} href={buildLink({ path: `/laboratorio/${laboratory.id}/` })} target="_blank" rel="noopener noreferrer">
              Ver exames e horários
            </Link>
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
}
